import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assessmentService } from "../services/assessmentService";
import type { AssessmentFormData, QuestionFormData, QuestionType, AudienceMode } from "../types/assessment";
import { companyService } from "@/features/company/services/companyService";
import { visaService } from "@/features/profile/services/visaService";

const MultiSelectDropdown = ({
  label,
  options,
  selected,
  onChange,
  placeholder,
  searchable = true,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  searchable?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="relative" ref={ref}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => e.key === "Enter" && setOpen((v) => !v)}
        className="w-full min-h-[38px] px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white flex flex-wrap gap-1 items-center"
      >
        {selected.length === 0 ? (
          <span className="text-gray-400 text-sm py-0.5 flex-1">{placeholder}</span>
        ) : (
          selected.map((v) => {
            const lbl = options.find((o) => o.value === v)?.label ?? v;
            return (
              <span key={v} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs font-medium">
                {lbl}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(v); }}
                  className="hover:text-indigo-900 leading-none"
                >×</button>
              </span>
            );
          })
        )}
        <svg className={`w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 flex flex-col max-h-56">
          {searchable && (
            <div className="p-2 border-b border-gray-100 flex-shrink-0">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400 italic">No results</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(opt.value); }}
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selected.includes(opt.value) ? "bg-indigo-600 border-indigo-600" : "border-gray-300"}`}>
                    {selected.includes(opt.value) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className={selected.includes(opt.value) ? "text-indigo-700 font-medium" : "text-gray-700"}>{opt.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

type Tab = "details" | "audience" | "questions" | "csv";

const defaultQuestion = (): QuestionFormData => ({
  question_order: 1,
  question_type: "multiple_choice",
  prompt: "",
  options: ["", "", "", ""],
  correct_answer: "",
  points: 1,
});

const defaultForm = (): AssessmentFormData => ({
  title: "",
  description: "",
  passing_score: 70,
  time_limit_seconds: null,
  allow_retake: true,
  audience_mode: "all",
  audience_country: [],
  audience_company: [],
  audience_batch: [],
  audience_visa_type: [],
  questions: [],
});

export function AssessmentBuilderPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [activeTab, setActiveTab] = useState<Tab>("details");
  const [form, setForm] = useState<AssessmentFormData>(defaultForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);

  // Audience dropdown options
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [visaTypeOptions, setVisaTypeOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    companyService.getDropdown().then(setCompanyOptions).catch(console.error);
    visaService.getVisaDropdown(i18n.language || "en").then(setVisaTypeOptions).catch(console.error);
  }, []);

  // CSV import state
  const [csvText, setCsvText] = useState("");
  const [csvPreview, setCsvPreview] = useState<QuestionFormData[]>([]);
  const [csvError, setCsvError] = useState("");

  useEffect(() => {
    if (!isEdit || !id) return;
    assessmentService.get(id).then((a) => {
      setForm({
        title: a.title,
        description: a.description ?? "",
        passing_score: a.passing_score,
        time_limit_seconds: a.time_limit_seconds,
        allow_retake: a.allow_retake,
        audience_mode: a.audience_mode,
        audience_country: a.audience_country ?? [],
        audience_company: a.audience_company ?? [],
        audience_batch: a.audience_batch ?? [],
        audience_visa_type: a.audience_visa_type ?? [],
        questions: (a.questions ?? []).map((q) => ({
          question_order: q.question_order,
          question_type: q.question_type,
          prompt: q.prompt,
          options: q.options ?? ["", "", "", ""],
          correct_answer: q.correct_answer ?? "",
          points: q.points,
        })),
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id, isEdit]);

  const setField = <K extends keyof AssessmentFormData>(key: K, value: AssessmentFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert(t("assessment.title_required"));
      return;
    }
    const missingAnswer = form.questions.findIndex(
      (q) => !q.correct_answer || q.correct_answer.trim() === ""
    );
    if (missingAnswer !== -1) {
      alert(t("assessment.correct_answer_required", { num: missingAnswer + 1, defaultValue: `Question ${missingAnswer + 1} is missing a correct answer.` }));
      setActiveTab("questions");
      setExpandedQuestion(missingAnswer);
      return;
    }
    setSaving(true);
    try {
      if (isEdit && id) {
        await assessmentService.update(id, form);
      } else {
        await assessmentService.create(form);
      }
      navigate("/assessments");
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // ── Question helpers ───────────────────────────────────────────────────────

  const addQuestion = () => {
    const q = defaultQuestion();
    q.question_order = form.questions.length + 1;
    setForm((prev) => ({ ...prev, questions: [...prev.questions, q] }));
    setExpandedQuestion(form.questions.length);
  };

  const updateQuestion = (idx: number, patch: Partial<QuestionFormData>) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    }));
  };

  const removeQuestion = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions
        .filter((_, i) => i !== idx)
        .map((q, i) => ({ ...q, question_order: i + 1 })),
    }));
    setExpandedQuestion(null);
  };

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= form.questions.length) return;
    setForm((prev) => {
      const qs = [...prev.questions];
      [qs[idx], qs[next]] = [qs[next], qs[idx]];
      return { ...prev, questions: qs.map((q, i) => ({ ...q, question_order: i + 1 })) };
    });
    setExpandedQuestion(next);
  };

  // ── CSV parsing ────────────────────────────────────────────────────────────

  const parseCSV = () => {
    setCsvError("");
    const lines = csvText.trim().split("\n").slice(1); // skip header
    if (lines.length === 0) {
      setCsvError(t("assessment.csv_empty"));
      return;
    }
    const parsed: QuestionFormData[] = [];
    for (let i = 0; i < lines.length; i++) {
      const cols = lines[i].split(",");
      if (cols.length < 9) {
        setCsvError(t("assessment.csv_row_error", { row: i + 2 }));
        return;
      }
      const [orderRaw, typeRaw, prompt, a, b, c, d, correct, pointsRaw] = cols.map((c) => c.trim());
      const order = parseInt(orderRaw, 10);
      const points = parseFloat(pointsRaw) || 1;
      const type = typeRaw as QuestionType;
      if (!["multiple_choice", "text", "true_false"].includes(type)) {
        setCsvError(t("assessment.csv_type_error", { row: i + 2, type }));
        return;
      }
      const optionsList = [a, b, c, d].filter(Boolean);
      const corrIdx = parseInt(correct, 10);
      const resolvedCorrect =
        !optionsList.includes(correct) &&
        !isNaN(corrIdx) &&
        corrIdx >= 1 &&
        corrIdx <= optionsList.length
          ? optionsList[corrIdx - 1]
          : correct;
      parsed.push({
        question_order: isNaN(order) ? i + 1 : order,
        question_type: type,
        prompt,
        options: optionsList,
        correct_answer: resolvedCorrect,
        points,
      });
    }
    setCsvPreview(parsed);
  };

  const importCSV = async () => {
    if (!id) {
      // For new assessments, merge into form
      setForm((prev) => ({
        ...prev,
        questions: [
          ...prev.questions,
          ...csvPreview.map((q, i) => ({
            ...q,
            question_order: prev.questions.length + i + 1,
          })),
        ],
      }));
      setCsvPreview([]);
      setCsvText("");
      setActiveTab("questions");
      return;
    }
    try {
      await assessmentService.importQuestions(id, csvPreview);
      setCsvPreview([]);
      setCsvText("");
      setActiveTab("questions");
      // Reload questions
      const updated = await assessmentService.get(id);
      setForm((prev) => ({
        ...prev,
        questions: (updated.questions ?? []).map((q) => ({
          question_order: q.question_order,
          question_type: q.question_type,
          prompt: q.prompt,
          options: q.options ?? [],
          correct_answer: q.correct_answer ?? "",
          points: q.points,
        })),
      }));
    } catch (e) {
      console.error(e);
    }
  };

  // ── Audience helpers ───────────────────────────────────────────────────────

  const commaSplit = (val: string) =>
    val
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  // ── Tabs ───────────────────────────────────────────────────────────────────

  const tabs: { key: Tab; label: string }[] = [
    { key: "details",   label: t("assessment.tab_details") },
    { key: "audience",  label: t("assessment.tab_audience") },
    { key: "questions", label: t("assessment.tab_questions") },
    { key: "csv",       label: t("assessment.tab_csv") },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400 text-sm">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? t("assessment.edit_title") : t("assessment.new_title")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isEdit ? t("assessment.edit_subtitle") : t("assessment.new_subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/assessments")}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {saving ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-6 gap-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.key
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── DETAILS TAB ──────────────────────────────────────────────────── */}
      {activeTab === "details" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("assessment.field_title")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t("assessment.field_title_placeholder")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("assessment.field_description")}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder={t("assessment.field_description_placeholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("assessment.field_passing_score")} (%)
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={form.passing_score}
                onChange={(e) => setField("passing_score", Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("assessment.field_time_limit")} ({t("assessment.minutes")})
              </label>
              <input
                type="number"
                min={0}
                value={form.time_limit_seconds != null ? Math.round(form.time_limit_seconds / 60) : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setField("time_limit_seconds", val === "" ? null : Number(val) * 60);
                }}
                placeholder={t("assessment.no_limit")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setField("allow_retake", !form.allow_retake)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                form.allow_retake ? "bg-indigo-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.allow_retake ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">{t("assessment.field_allow_retake")}</span>
          </div>
        </div>
      )}

      {/* ── AUDIENCE TAB ─────────────────────────────────────────────────── */}
      {activeTab === "audience" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">{t("assessment.field_audience_mode")}</p>
            <div className="flex gap-6">
              {(["all", "filtered"] as AudienceMode[]).map((mode) => (
                <label key={mode} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="audience_mode"
                    value={mode}
                    checked={form.audience_mode === mode}
                    onChange={() => setField("audience_mode", mode)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">
                    {mode === "all" ? t("assessment.audience_all") : t("assessment.audience_filtered")}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {form.audience_mode === "filtered" && (
            <div className="space-y-4 pt-2">
              <p className="text-xs text-gray-500">{t("assessment.audience_filter_hint")}</p>

              <MultiSelectDropdown
                label={t("assessment.field_audience_country")}
                options={["JP","PH","VN","ID","MM","KH","TH","CN","NP","IN","BD","LK"].map((code) => ({
                  value: code,
                  label: t(`profile.country_${code.toLowerCase()}`, { defaultValue: code }),
                }))}
                selected={form.audience_country}
                onChange={(v) => setField("audience_country", v)}
                placeholder={t("access.filter_all")}
              />

              <MultiSelectDropdown
                label={t("assessment.field_audience_company")}
                options={companyOptions}
                selected={form.audience_company}
                onChange={(v) => setField("audience_company", v)}
                placeholder={t("inquiry.search_company")}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("assessment.field_audience_batch")}
                </label>
                <input
                  type="text"
                  value={(form.audience_batch as string[]).join(", ")}
                  onChange={(e) => setField("audience_batch", commaSplit(e.target.value))}
                  placeholder={t("assessment.comma_separated")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {(form.audience_batch as string[]).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(form.audience_batch as string[]).map((v) => (
                      <span key={v} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700">
                        {v}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <MultiSelectDropdown
                label={t("assessment.field_audience_visa_type")}
                options={visaTypeOptions}
                selected={form.audience_visa_type}
                onChange={(v) => setField("audience_visa_type", v)}
                placeholder={t("access.filter_all")}
              />
            </div>
          )}
        </div>
      )}

      {/* ── QUESTIONS TAB ────────────────────────────────────────────────── */}
      {activeTab === "questions" && (
        <div className="space-y-3">
          {form.questions.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
              <p className="text-gray-400 text-sm">{t("assessment.no_questions")}</p>
            </div>
          )}

          {form.questions.map((q, idx) => {
            const isOpen = expandedQuestion === idx;
            return (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {/* Question header */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedQuestion(isOpen ? null : idx)}
                >
                  <span className="text-xs font-semibold text-gray-400 w-6 text-center">
                    {q.question_order}
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-800 truncate">
                    {q.prompt || t("assessment.untitled_question")}
                  </span>
                  <span className="text-xs text-gray-400 bg-gray-100 rounded px-2 py-0.5">
                    {q.question_type}
                  </span>
                  <span className="text-xs text-gray-400">{q.points}pt</span>

                  <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => moveQuestion(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move up"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveQuestion(idx, 1)}
                      disabled={idx === form.questions.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move down"
                    >
                      ▼
                    </button>
                    <button
                      onClick={() => removeQuestion(idx)}
                      className="p-1 text-red-400 hover:text-red-600"
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Question editor */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-gray-50 space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("assessment.q_type")}
                        </label>
                        <select
                          value={q.question_type}
                          onChange={(e) =>
                            updateQuestion(idx, {
                              question_type: e.target.value as QuestionType,
                              options:
                                e.target.value === "true_false"
                                  ? ["True", "False"]
                                  : e.target.value === "text"
                                  ? []
                                  : ["", "", "", ""],
                              correct_answer: "",
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="multiple_choice">{t("assessment.type_mc")}</option>
                          <option value="true_false">{t("assessment.type_tf")}</option>
                          <option value="text">{t("assessment.type_text")}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("assessment.q_points")}
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={q.points}
                          onChange={(e) => updateQuestion(idx, { points: Number(e.target.value) })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t("assessment.q_prompt")}
                      </label>
                      <textarea
                        value={q.prompt}
                        onChange={(e) => updateQuestion(idx, { prompt: e.target.value })}
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                        placeholder={t("assessment.q_prompt_placeholder")}
                      />
                    </div>

                    {(q.question_type === "multiple_choice" || q.question_type === "true_false") && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          {t("assessment.q_options")}
                        </label>
                        <div className="space-y-2">
                          {q.options.map((opt, oIdx) => (
                            <div key={oIdx} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`correct-${idx}`}
                                checked={q.correct_answer === opt && opt !== ""}
                                onChange={() => updateQuestion(idx, { correct_answer: opt })}
                                className="text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                                disabled={q.question_type === "true_false"}
                              />
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => {
                                  const newOpts = [...q.options];
                                  const newText = e.target.value;
                                  newOpts[oIdx] = newText;
                                  const patch: Partial<QuestionFormData> = { options: newOpts };
                                  if (q.correct_answer === opt) patch.correct_answer = newText;
                                  updateQuestion(idx, patch);
                                }}
                                readOnly={q.question_type === "true_false"}
                                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 read-only:bg-gray-50"
                                placeholder={`${t("assessment.option")} ${oIdx + 1}`}
                              />
                              {q.question_type === "true_false" && (
                                <input
                                  type="radio"
                                  name={`correct-${idx}`}
                                  checked={q.correct_answer === opt}
                                  onChange={() => updateQuestion(idx, { correct_answer: opt })}
                                  className="text-indigo-600 focus:ring-indigo-500"
                                />
                              )}
                            </div>
                          ))}
                          {q.question_type === "multiple_choice" && (
                            <button
                              type="button"
                              onClick={() =>
                                updateQuestion(idx, { options: [...q.options, ""] })
                              }
                              className="text-xs text-indigo-600 hover:underline mt-1"
                            >
                              + {t("assessment.add_option")}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">{t("assessment.q_correct_hint")}</p>
                      </div>
                    )}

                    {q.question_type === "text" && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("assessment.q_correct_answer")}
                        </label>
                        <input
                          type="text"
                          value={q.correct_answer}
                          onChange={(e) => updateQuestion(idx, { correct_answer: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder={t("assessment.q_correct_placeholder")}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={addQuestion}
            className="w-full py-2.5 border-2 border-dashed border-gray-200 text-gray-400 text-sm rounded-xl hover:border-indigo-300 hover:text-indigo-500 transition-colors"
          >
            + {t("assessment.add_question")}
          </button>
        </div>
      )}

      {/* ── CSV IMPORT TAB ───────────────────────────────────────────────── */}
      {activeTab === "csv" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">{t("assessment.csv_format")}</h3>
            <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono text-gray-600 overflow-x-auto">
              order,type,prompt,optionA,optionB,optionC,optionD,correctAnswer,points
              <br />
              1,multiple_choice,What is 2+2?,2,4,6,8,4,1
              <br />
              2,true_false,The sky is blue.,True,False,,,True,1
              <br />
              3,text,Describe photosynthesis.,,,,,Light reaction,2
            </div>
            <p className="text-xs text-gray-400 mt-2">{t("assessment.csv_hint")}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("assessment.csv_paste")}
            </label>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={8}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder={t("assessment.csv_placeholder")}
            />
            {csvError && <p className="text-xs text-red-500 mt-1">{csvError}</p>}
          </div>

          <button
            onClick={parseCSV}
            className="bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {t("assessment.csv_parse")}
          </button>

          {csvPreview.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                {t("assessment.csv_preview")} ({csvPreview.length})
              </h3>
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-500">#</th>
                      <th className="px-3 py-2 text-left text-gray-500">{t("assessment.q_type")}</th>
                      <th className="px-3 py-2 text-left text-gray-500">{t("assessment.q_prompt")}</th>
                      <th className="px-3 py-2 text-left text-gray-500">{t("assessment.q_correct_answer")}</th>
                      <th className="px-3 py-2 text-left text-gray-500">{t("assessment.q_points")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {csvPreview.map((q, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600">{q.question_order}</td>
                        <td className="px-3 py-2 text-gray-600">{q.question_type}</td>
                        <td className="px-3 py-2 text-gray-800 max-w-xs truncate">{q.prompt}</td>
                        <td className="px-3 py-2 text-gray-600">{q.correct_answer}</td>
                        <td className="px-3 py-2 text-gray-600">{q.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={importCSV}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                {t("assessment.csv_import")} ({csvPreview.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
